// Canonical assessment definitions for the IEP form and PDF generator.
//
// An IEP can include up to two assessment entries (e.g., one VB-MAPP at intake
// + one ABLLS-R follow-up). Each entry has a `type` and a per-section map of
// `{ overall, domains: { [key]: { score, notes } } }`.

export const VBMAPP_MILESTONE_DOMAINS = [
  { key: 'manding', label: 'Manding (asking or protesting)' },
  { key: 'tacting', label: 'Tacting (naming, expressive labeling)' },
  { key: 'listener', label: 'Listener Skill (understanding words, receptive labeling)' },
  { key: 'vpMts', label: 'Visual Perceptual & Matching-To-Sample (VP-MTS)' },
  { key: 'independentPlay', label: 'Independent Play Skills' },
  { key: 'socialPlay', label: 'Social Skills and Social Play' },
  { key: 'motorImitation', label: 'Motor Imitation' },
  { key: 'echoic', label: 'Echoic (vocal imitation)' },
  { key: 'vocalBehavior', label: 'Spontaneous Vocal Behavior & Linguistic Skills' },
  { key: 'reading', label: 'Reading Skills' },
  { key: 'math', label: 'Math Skills' },
  { key: 'writing', label: 'Writing Skills' },
  { key: 'groupSkills', label: 'School Readiness / Group Skills' }
];

export const VBMAPP_BARRIER_DOMAINS = [
  { key: 'negativeBehaviors', label: 'Negative Behaviors' },
  { key: 'instructionalControl', label: 'Instructional Control (Escape & Avoidance)' },
  { key: 'impairedMand', label: 'Impaired Mand Repertoire' },
  { key: 'impairedTact', label: 'Impaired Tact Repertoire' },
  { key: 'impairedMotorImitation', label: 'Impaired Motor Imitation' },
  { key: 'impairedEchoic', label: 'Impaired Echoic Repertoire' },
  { key: 'impairedVpMts', label: 'Impaired Visual Perceptual & Matching-to-sample' },
  { key: 'impairedListener', label: 'Impaired Listener Repertoires' },
  { key: 'impairedIntraverbal', label: 'Impaired Intraverbal Repertoire' },
  { key: 'impairedSocial', label: 'Impaired Social Skills' },
  { key: 'promptDependent', label: 'Prompt Dependent' },
  { key: 'scrollingResponses', label: 'Scrolling Responses' },
  { key: 'impairedScanning', label: 'Impaired Scanning Skills' },
  { key: 'failureCDs', label: 'Failure to Make Conditional Discriminations (CDs)' },
  { key: 'failureGeneralize', label: 'Failure to Generalize' },
  { key: 'weakMOs', label: 'Weak Motivating Operations (MOs)' },
  { key: 'responseWeakensMo', label: 'Response Requirement Weakens the MO' },
  { key: 'reinforcementDependent', label: 'Reinforcement Dependent' },
  { key: 'selfStimulation', label: 'Self-Stimulation' },
  { key: 'articulation', label: 'Articulation Problems' },
  { key: 'ocBehavior', label: 'Obsessive-Compulsive Behavior' },
  { key: 'hyperactive', label: 'Hyperactive Behavior' },
  { key: 'eyeContact', label: 'Failure to Make Eye Contact / Attend to People' },
  { key: 'sensoryDefensiveness', label: 'Sensory Defensiveness' }
];

// ABLLS-R top-level areas (A–Y). Each area is scored 0–10 with observation notes.
export const ABLLS_AREAS = [
  { key: 'A', label: 'A. Cooperation and Reinforcer Effectiveness' },
  { key: 'B', label: 'B. Visual Performance' },
  { key: 'C', label: 'C. Receptive Language' },
  { key: 'D', label: 'D. Motor Imitation' },
  { key: 'E', label: 'E. Vocal Imitation' },
  { key: 'F', label: 'F. Requests (Mands)' },
  { key: 'G', label: 'G. Labeling (Tacts)' },
  { key: 'H', label: 'H. Intraverbals' },
  { key: 'I', label: 'I. Spontaneous Vocalizations' },
  { key: 'J', label: 'J. Syntax and Grammar' },
  { key: 'K', label: 'K. Play and Leisure' },
  { key: 'L', label: 'L. Social Interaction' },
  { key: 'M', label: 'M. Group Instruction' },
  { key: 'N', label: 'N. Follow Classroom Routines' },
  { key: 'O', label: 'O. Generalized Responding' },
  { key: 'P', label: 'P. Reading' },
  { key: 'Q', label: 'Q. Math' },
  { key: 'R', label: 'R. Writing' },
  { key: 'S', label: 'S. Spelling' },
  { key: 'T', label: 'T. Dressing' },
  { key: 'U', label: 'U. Eating' },
  { key: 'V', label: 'V. Grooming' },
  { key: 'W', label: 'W. Toileting' },
  { key: 'X', label: 'X. Gross Motor' },
  { key: 'Y', label: 'Y. Fine Motor' }
];

// Registry of assessment types. `sections` is rendered in order in both the
// form and the PDF. `maxScore` is the per-domain ceiling shown in the UI;
// `maxOverall` is what we display next to the overall summary field.
export const ASSESSMENT_TYPES = {
  vbmapp: {
    key: 'vbmapp',
    label: 'VB-MAPP',
    sections: [
      {
        key: 'milestones',
        label: 'Skills Assessments',
        labelKey: 'iep.assessment.section.milestones',
        maxScore: 15,
        maxOverall: 170,
        domains: VBMAPP_MILESTONE_DOMAINS
      },
      {
        key: 'barriers',
        label: 'Barriers Assessments',
        labelKey: 'iep.assessment.section.barriers',
        maxScore: 4,
        maxOverall: 96,
        domains: VBMAPP_BARRIER_DOMAINS
      }
    ]
  },
  ablls: {
    key: 'ablls',
    label: 'ABLLS-R',
    sections: [
      {
        key: 'skills',
        label: 'Skill Areas (A–Y)',
        labelKey: 'iep.assessment.section.ablls',
        maxScore: 10,
        maxOverall: 250,
        domains: ABLLS_AREAS
      }
    ]
  }
};

export const ASSESSMENT_TYPE_OPTIONS = Object.values(ASSESSMENT_TYPES).map(({ key, label }) => ({ key, label }));

const blankSectionsFor = (typeKey) => {
  const def = ASSESSMENT_TYPES[typeKey];
  if (!def) return {};
  return def.sections.reduce((acc, s) => ({ ...acc, [s.key]: { overall: '', rows: [] } }), {});
};

// Stable random id so React keys survive re-renders during edits.
const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `a_${Date.now()}_${Math.random().toString(36).slice(2)}`;

export const createBlankAssessment = (typeKey = '') => ({
  id: newId(),
  type: typeKey,
  isNA: false,
  naReason: '',
  sections: typeKey ? blankSectionsFor(typeKey) : {}
});

// Build a single blank row inside an assessment section. Domain key starts
// empty so the dropdown forces an explicit choice.
export const createBlankRow = () => ({ rowId: newId(), domainKey: '', score: '', notes: '' });

// When an entry's type changes, rebuild the sections map for the new type
// without losing any other entry-level state. Existing rows from the old type
// are dropped because their domainKeys won't match the new type's options.
export const reshapeAssessmentForType = (entry, typeKey) => ({
  ...entry,
  type: typeKey,
  sections: typeKey ? blankSectionsFor(typeKey) : {}
});

// Migrate legacy IEP form data (flat `mandingScore` / `negativeBehaviorsScore` fields,
// sectionNA p4/p5) into a single VB-MAPP entry in the new `assessments` array.
// Only domains that actually had a score or note become rows — empty domains
// are dropped so the migrated form matches the row-on-demand UX.
// Returns null if no legacy data is present so callers can skip the rewrite.
export const migrateLegacyAssessments = (formData, sectionNA) => {
  if (!formData) return null;
  if (Array.isArray(formData.assessments) && formData.assessments.length > 0) return null;

  const buildRows = (domainDefs) =>
    domainDefs.reduce((rows, d) => {
      const score = formData[`${d.key}Score`];
      const notes = formData[`${d.key}Notes`];
      const hasScore = score !== undefined && score !== '' && score !== null;
      const hasNotes = typeof notes === 'string' && notes !== '';
      if (hasScore || hasNotes) {
        rows.push({ rowId: newId(), domainKey: d.key, score: hasScore ? String(score) : '', notes: notes || '' });
      }
      return rows;
    }, []);

  const milestoneRows = buildRows(VBMAPP_MILESTONE_DOMAINS);
  const barrierRows = buildRows(VBMAPP_BARRIER_DOMAINS);

  const overallMilestones = formData.milestonesOverallScore ?? '';
  const overallBarriers = formData.barriersOverallScore ?? '';
  const milestonesNA = !!sectionNA?.p4?.isNA;
  const barriersNA = !!sectionNA?.p5?.isNA;
  const milestonesReason = sectionNA?.p4?.comment || '';
  const barriersReason = sectionNA?.p5?.comment || '';

  const hasAny =
    milestoneRows.length > 0 || barrierRows.length > 0 || overallMilestones !== '' || overallBarriers !== '' || milestonesNA || barriersNA;
  if (!hasAny) return null;

  const entry = createBlankAssessment('vbmapp');
  entry.sections.milestones = { overall: overallMilestones, rows: milestoneRows };
  entry.sections.barriers = { overall: overallBarriers, rows: barrierRows };
  // If either VB-MAPP sub-section was marked NA in the legacy shape, surface
  // that on the new entry. Combine the two reason strings if both were set.
  if (milestonesNA || barriersNA) {
    entry.isNA = true;
    entry.naReason = [milestonesReason && `Milestones: ${milestonesReason}`, barriersReason && `Barriers: ${barriersReason}`]
      .filter(Boolean)
      .join(' — ');
  }
  return [entry];
};

// Strip legacy flat fields from the saved payload once the data has been migrated.
// Keeps the stored JSON tidy and prevents the two shapes from drifting.
export const LEGACY_ASSESSMENT_FIELDS = [
  ...VBMAPP_MILESTONE_DOMAINS.flatMap((d) => [`${d.key}Score`, `${d.key}Notes`]),
  ...VBMAPP_BARRIER_DOMAINS.flatMap((d) => [`${d.key}Score`, `${d.key}Notes`]),
  'milestonesOverallScore',
  'barriersOverallScore'
];
